import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import Icon from "../components/ui/Icon";

// ---------------------------------------------------------------------------
// โครงหน้าหลังบ้านของ LucidCRM — icon rail (แถบไอคอนแคบซ้ายสุด) + แผงเมนูย่อย + topbar บาง
//   เดสก์ท็อป : rail 68px อยู่ซ้าย กดไอคอนหมวดแล้วเปิดแผงเมนูย่อย (หุบได้ จำค่าไว้ในเครื่อง)
//   มือถือ    : rail กลายเป็นแถบล่าง (bottom tab) ส่วนแผงเมนูย่อยเป็น drawer เลื่อนขึ้นเหนือแถบ
//
//   groups = [{ id, title, short, railIcon, items: [{ to, label, icon, exact, locked, lockTitle }] }]
//   panelTop = ส่วนควบคุมที่โชว์เฉพาะมือถือในแผง (ร้าน/แพ็กเกจ/สาขา/ภาษา)
// ---------------------------------------------------------------------------

const STORE_KEY = "lucid:panel-open";

const isMobile = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(max-width: 768px)").matches;

const readPref = () => {
  if (typeof window === "undefined" || isMobile()) return false;
  try {
    const saved = window.localStorage.getItem(STORE_KEY);
    if (saved === "1") return true;
    if (saved === "0") return false;
  } catch (e) {
    // ใช้ค่าเริ่มต้น
  }
  return window.innerWidth >= 1100;
};

const savePref = (value) => {
  if (isMobile()) return;
  try {
    window.localStorage.setItem(STORE_KEY, value ? "1" : "0");
  } catch (e) {
    // ไม่เป็นไร
  }
};

const matches = (item, pathname) => (item.exact ? pathname === item.to : pathname.startsWith(item.to));

/** หมวดที่เมนูตรงกับ URL ปัจจุบันที่สุด (ยึด path ยาวสุด) */
const groupOfPath = (groups, pathname) => {
  let best = null;
  groups.forEach((group) =>
    group.items.forEach((item) => {
      if (matches(item, pathname) && (!best || item.to.length > best.len)) best = { id: group.id, len: item.to.length };
    })
  );
  return best ? best.id : null;
};

export default function Shell({ groups, brand, brandTo, title, topLeft, topRight, panelTop, children, style, navLabel, toggleLabel, closeLabel, lockedHint }) {
  const { pathname } = useLocation();
  const routeGroup = useMemo(() => groupOfPath(groups, pathname), [groups, pathname]);
  const [activeId, setActiveId] = useState(routeGroup || groups[0]?.id);
  const [panelOpen, setPanelOpen] = useState(readPref);

  // เปลี่ยนหน้า -> หมวดตามหน้านั้น · มือถือปิด drawer
  useEffect(() => {
    if (routeGroup) setActiveId(routeGroup);
    if (isMobile()) setPanelOpen(false);
  }, [pathname, routeGroup]);

  const current = groups.find((group) => group.id === activeId) || groups[0];

  const setOpen = (value) => {
    setPanelOpen(value);
    savePref(value);
  };

  const onRail = (id) => {
    if (id === current?.id && panelOpen) {
      setOpen(false);
      return;
    }
    setActiveId(id);
    setOpen(true);
  };

  return (
    <div className={`lx-shell ${panelOpen ? "is-panel-open" : ""}`} style={style}>
      <nav className="lx-rail" aria-label={navLabel}>
        <Link to={brandTo} className="lx-rail__brand" aria-label="home">
          {brand}
        </Link>
        <div className="lx-rail__items">
          {groups.map((group) => (
            <button
              key={group.id}
              type="button"
              className={`lx-rail__btn ${group.id === current?.id && panelOpen ? "is-open" : ""} ${group.id === routeGroup ? "is-current" : ""}`}
              title={group.title}
              aria-label={group.title}
              aria-expanded={group.id === current?.id && panelOpen}
              onClick={() => onRail(group.id)}
            >
              <Icon name={group.railIcon} />
              <span className="lx-rail__label">{group.short}</span>
            </button>
          ))}
        </div>
      </nav>

      <aside className="lx-panel" aria-label={current?.title}>
        <div className="lx-panel__head">
          <div className="lx-panel__title">{current?.title}</div>
          <button type="button" className="lx-iconbtn" onClick={() => setOpen(false)} aria-label={closeLabel} title={closeLabel}>
            <Icon name="close" />
          </button>
        </div>

        {panelTop ? <div className="lx-panel__ctx">{panelTop}</div> : null}

        <div className="lx-panel__scroll">
          {current?.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              title={item.locked ? lockedHint : undefined}
              className={({ isActive }) => `lx-nav ${item.locked ? "is-locked" : ""} ${isActive ? "is-active" : ""}`}
            >
              <span className="lx-nav__icon"><Icon name={item.icon} /></span>
              <span className="lx-nav__text">{item.label}</span>
              {item.locked ? <span className="lx-nav__lock"><Icon name="lock" /></span> : null}
            </NavLink>
          ))}
        </div>
      </aside>

      {panelOpen ? <div className="lx-backdrop" onClick={() => setOpen(false)} /> : null}

      <div className="lx-main">
        <header className="lx-topbar">
          <button type="button" className="lx-iconbtn lx-topbar__toggle" onClick={() => setOpen(!panelOpen)} aria-label={toggleLabel} title={toggleLabel}>
            <Icon name="menu" />
          </button>
          <Link to={brandTo} className="lx-topbar__brand" aria-label="home">{brand}</Link>
          {topLeft}
          <h2 className="lx-topbar__title">{title}</h2>
          <div className="lx-topbar__spacer" />
          {topRight}
        </header>

        <main className="lx-content">{children}</main>
      </div>
    </div>
  );
}
